import { StrictLocalMsg } from "@faire/localization/react/StrictLocalMsg";
import { CheckOutline } from "@faire/slate-icons/CheckOutline";
import { exhaustiveSwitch } from "@faire/typescript";
import React from "react";

import { SpinnerBase } from "../loading-spinner/SpinnerBase";

import { FormFieldValidationStatus } from "./types";

type IProps = {
  value: string | undefined;
  status: FormFieldValidationStatus | undefined;
};

const ValidatingStatusIndicator: React.FC<Omit<IProps, "status">> = ({
  value,
}) => (
  <>
    <SpinnerBase size="small" color="var(--slate-color-icon-primary)" />
    <span className="sr-only" data-ph-no-capture>
      <StrictLocalMsg
        id="zcqv7k"
        defaultMessage="Validating {valueFullyTranslatedSentence}"
        description={{
          text: "Screen reader accessible description of a input that's currently validating the user's input",
        }}
        values={{
          valueFullyTranslatedSentence: value ?? "",
        }}
      />
    </span>
  </>
);

const ValidatedStatusIndicator: React.FC<Omit<IProps, "status">> = ({
  value,
}) => (
  <>
    <CheckOutline color="var(--slate-color-icon-primary)" strokeWidth={2} />
    <span className="sr-only" data-ph-no-capture>
      <StrictLocalMsg
        id="l41WzW"
        defaultMessage="Validated {valueFullyTranslatedSentence}"
        description={{
          text: "Screen reader accessible description of a input that's currently validating the user's input",
        }}
        values={{
          valueFullyTranslatedSentence: value ?? "",
        }}
      />
    </span>
  </>
);

const getStatusIndicator = (status: FormFieldValidationStatus | undefined) => {
  switch (status) {
    case "validating":
      return ValidatingStatusIndicator;
    case "validated":
      return ValidatedStatusIndicator;
    case undefined:
      return;
    default:
      return exhaustiveSwitch(status);
  }
};

export const FormFieldValidationStatusIndicator: React.FC<IProps> = ({
  status,
  value,
}) => {
  const StatusIndicator = getStatusIndicator(status);
  // NOTE: for screen readers to properly announce aria live regions, they should always be rendered and their content is what should be conditionally shown / hidden
  return (
    <div role="status" aria-live="polite" className="flex">
      {StatusIndicator ? <StatusIndicator value={value} /> : null}
    </div>
  );
};
